const GCS_BASE = 'https://storage.googleapis.com/e3-webshop-image-storage';

export type GumletImageParams = {
  width?: number;
  height?: number;
  quality?: number;
  format?: 'auto' | 'webp' | 'avif' | 'jpg' | 'png';
  fit?: 'cover' | 'contain' | 'fill';
};

/** Width/quality presets sized to rendered boxes (2× for high-DPR). */
export const GUMLET_PRESETS = {
  heroCarousel: { width: 1280, quality: 75 },
  fullBleedHero: { width: 1920, quality: 80 },
  heroBackground: { width: 1920, quality: 75 },
  /** Recipe detail hero: 16/10 box capped at 520px tall (~832px CSS, 1280 for 1.5–2×). */
  recipeHero: { width: 1280, quality: 75 },
  thumbnail: { width: 384, quality: 70 },
  categoryCard: { width: 600, quality: 75 },
  productCard: { width: 516, quality: 75 },
  gridTile: { width: 600, quality: 75 },
} as const satisfies Record<string, GumletImageParams>;

/**
 * Converts a GCS image URL to a Gumlet-optimized URL.
 * Falls back to the original URL if Gumlet is not configured or the URL is not from GCS.
 */
export function buildGumletUrl(src: string | undefined, params: GumletImageParams = {}): string {
  if (!src) return '';

  const gumletBase = process.env.NEXT_PUBLIC_GUMLET_SOURCE_URL;
  if (!gumletBase || !src.startsWith(GCS_BASE)) {
    return src;
  }

  const path = src.slice(GCS_BASE.length);
  const url = new URL(`${gumletBase}${path}`);

  if (params.width) url.searchParams.set('w', String(params.width));
  if (params.height) url.searchParams.set('h', String(params.height));
  if (params.quality) url.searchParams.set('quality', String(params.quality));
  url.searchParams.set('format', params.format ?? 'auto');
  if (params.fit) url.searchParams.set('fit', params.fit);
  url.searchParams.set('compress', 'true');

  return url.toString();
}

export function resolveOptimizedImage(
  src: string | undefined,
  preset: keyof typeof GUMLET_PRESETS = 'heroBackground',
): string {
  if (!src) return '';
  return buildGumletUrl(src, GUMLET_PRESETS[preset]);
}
